/**
 * Effektive B2B-priser: kombinerer pris-input (usePriceData) med den rene
 * domænelogik (resolveVariantPrice). UI må KUN vise resultatet — aldrig regne selv.
 *
 * Antalsafhængig: `resolve` tager valgfrit antal (+ mix & match-kollektions-
 * antal), og `breaks` giver synlige mængdetrin (priceBreaks) til trin-tabeller.
 */
import { useCallback, useMemo } from "react";
import {
  priceBreaks,
  resolveVariantPrice,
  type PriceBreak,
  type ResolvedVariantPrice,
} from "@/domain";
import { useActivePriceData } from "./usePriceData";
import { useProductCollectionMap, useProducts } from "./useCatalog";
import type { ShopifyVariant } from "../types";

export interface EffectivePricing {
  isLoading: boolean;
  /**
   * Opløs pris for en variant på et givet produkt.
   * @param quantity valgfrit antal (trin-priser/-rabatter); default 1.
   * @param collectionQuantities valgfrit mix & match-antal pr. kollektion.
   */
  resolve: (
    variant: Pick<ShopifyVariant, "id" | "price">,
    productId: string,
    quantity?: number,
    collectionQuantities?: ReadonlyMap<string, number>,
  ) => ResolvedVariantPrice;
  /** Synlige mængdetrin for en variant ([] hvis ingen). */
  breaks: (
    variant: Pick<ShopifyVariant, "id" | "price">,
    productId: string,
  ) => PriceBreak[];
  /** Cashback-% for en variant (firma-regel vinder; ellers bedste pakke-regel). */
  cashbackPct: (variantId: string, productId: string) => number;
  /** Højeste cashback-% blandt et produkts varianter (til badge på kort). */
  productCashbackPct: (variantIds: readonly string[], productId: string) => number;
}

export function useEffectivePrices(): EffectivePricing {
  const priceData = useActivePriceData();
  const collectionMap = useProductCollectionMap();

  const buildInput = useCallback(
    (
      variant: Pick<ShopifyVariant, "id" | "price">,
      productId: string,
      quantity?: number,
      collectionQuantities?: ReadonlyMap<string, number>,
    ) => {
      const listPrice = Number.parseFloat(variant.price);
      return {
        shopifyVariantId: variant.id,
        listPriceInclVat: Number.isFinite(listPrice) && listPrice >= 0 ? listPrice : 0,
        companyVariantPrices: priceData.companyVariantPrices,
        packagePrices: priceData.packagePrices,
        accessRules: priceData.accessRules,
        productCollectionIds: collectionMap.get(productId) ?? [],
        defaultDiscountPct: priceData.defaultDiscountPct,
        quantity,
        collectionQuantities,
      };
    },
    [priceData, collectionMap],
  );

  const resolve = useCallback(
    (
      variant: Pick<ShopifyVariant, "id" | "price">,
      productId: string,
      quantity?: number,
      collectionQuantities?: ReadonlyMap<string, number>,
    ): ResolvedVariantPrice =>
      resolveVariantPrice(buildInput(variant, productId, quantity, collectionQuantities)),
    [buildInput],
  );

  const breaks = useCallback(
    (variant: Pick<ShopifyVariant, "id" | "price">, productId: string): PriceBreak[] =>
      priceBreaks(buildInput(variant, productId)),
    [buildInput],
  );

  /** Bedste pakke-cashback-% for et produkt (PRODUCT-match eller COLLECTION-medlemskab). */
  const packagePctForProduct = useCallback(
    (productId: string): number => {
      let best = 0;
      const productCollections = collectionMap.get(productId);
      for (const rule of priceData.packageCashbackRules) {
        if (rule.accessType === "PRODUCT") {
          if (rule.shopifyProductId === productId && rule.cashbackPct > best) {
            best = rule.cashbackPct;
          }
        } else if (rule.shopifyCollectionId && productCollections?.includes(rule.shopifyCollectionId)) {
          if (rule.cashbackPct > best) best = rule.cashbackPct;
        }
      }
      return best;
    },
    [priceData.packageCashbackRules, collectionMap],
  );

  const cashbackPct = useCallback(
    (variantId: string, productId: string) =>
      priceData.cashbackPctByVariant.get(variantId) ?? packagePctForProduct(productId),
    [priceData.cashbackPctByVariant, packagePctForProduct],
  );

  const productCashbackPct = useCallback(
    (variantIds: readonly string[], productId: string) => {
      const companyBest = variantIds.reduce(
        (best, id) => Math.max(best, priceData.cashbackPctByVariant.get(id) ?? 0),
        0,
      );
      return companyBest > 0 ? companyBest : packagePctForProduct(productId);
    },
    [priceData.cashbackPctByVariant, packagePctForProduct],
  );

  return useMemo(
    () => ({ isLoading: priceData.isLoading, resolve, breaks, cashbackPct, productCashbackPct }),
    [priceData.isLoading, resolve, breaks, cashbackPct, productCashbackPct],
  );
}

/** Effektiv pris for én variant-id (slår produkt og variant op i kataloget). */
export function useEffectivePrice(variantId: string | null | undefined): {
  isLoading: boolean;
  price: ResolvedVariantPrice | null;
} {
  const { data: products, isLoading: productsLoading } = useProducts();
  const pricing = useEffectivePrices();

  const price = useMemo(() => {
    if (!variantId || !products) return null;
    for (const product of products) {
      const variant = product.variants.find((v) => v.id === variantId);
      if (variant) return pricing.resolve(variant, product.id);
    }
    return null;
  }, [variantId, products, pricing]);

  return { isLoading: productsLoading || pricing.isLoading, price };
}
